/**
 * SummonVFXHandler — summon-placement presentation, extracted from
 * VFXSystem (decomposition arc, 2026-06-12: "granular, usable, modular").
 *
 * Owns the full summon visual lifecycle:
 *  - `vfx:summon_placed`    → pulsing ground ring (trap red / totem green /
 *                             default blue), infinite sine yoyo
 *  - `vfx:summon_triggered` → flash + expand, then destroy
 *  - `vfx:summon_expired`   → fade out, then destroy
 *  - `combat:summon_armed`  → proximity watch; `update()` (ticked from
 *                             VFXSystem.update) fires `onTrigger` when a mob
 *                             enters the armed radius
 *
 * Scene access goes through `getScene` so a mid-session scene swap
 * (VFXSystem.setScene) is picked up without re-wiring.
 */
import { createGraphics } from '../../rendering/utils/Graphics';
import { Tween, Easing } from '../../rendering/Tween';
import type { IGraphics } from '../../rendering/IRenderer';
import { SUMMON_RING_CONFIG, summonRingColor } from '../../../config/abilities/summonRing.config';

interface SummonRingVisual {
  gfx: IGraphics;
  baseRadius: number;
  baseColor: number;
  baseAlpha: number;
  state: { alpha: number; scale: number };
  tween: Tween | null;
  rafId: number | null;
}

interface ArmedSummon {
  instanceId: string;
  ownerId: string;
  x: number;
  y: number;
  radius: number;
  onTrigger: () => void;
}

export class SummonVFXHandler {
  // Substrate-aware summon ring visuals. Each carries its own active
  // Tween + rAF id so triggered/expired can cancel cleanly.
  private summonRings = new Map<string, SummonRingVisual>();
  private armedSummons: ArmedSummon[] = [];

  constructor(private getScene: () => unknown) {}

  /** Install subscriptions via the owner's tracked `sub` helper. */
  register(sub: (event: string, handler: (data: any) => void) => void): void {
    sub('vfx:summon_placed', (data) => this.onSummonPlaced(data));
    sub('vfx:summon_triggered', (data) => this.onSummonTriggered(data));
    sub('vfx:summon_expired', (data) => this.onSummonExpired(data));
    sub('combat:summon_armed', (data) => this.onSummonArmed(data));
  }

  private onSummonArmed(data: any): void {
    this.armedSummons.push({
      instanceId: data.instanceId,
      ownerId: data.ownerId,
      x: data.x,
      y: data.y,
      radius: data.radius,
      onTrigger: data.onTrigger,
    });
  }

  private onSummonPlaced(data: { type: string; x: number; y: number; radius: number; instanceId: string; color?: number; exact?: boolean }): void {
    const cfg = SUMMON_RING_CONFIG;
    const color = data.color ?? summonRingColor(data.type);
    const baseAlpha = cfg.fillAlpha;
    // Ring graphics — fill + stroke. Position is at (x, y); shape drawn
    // around the origin so setScale applies cleanly to scale.
    const gfx = createGraphics(this.getScene(), { depth: cfg.depth });
    gfx.setPosition(data.x, data.y);
    gfx.fillCircle(0, 0, data.radius, color, baseAlpha);
    gfx.strokeCircle(0, 0, data.radius, color, cfg.strokeWidth, cfg.strokeAlpha);

    const visual: SummonRingVisual = {
      gfx,
      baseRadius: data.radius,
      baseColor: color,
      baseAlpha,
      state: { alpha: 1, scale: 1 },
      tween: null,
      rafId: null,
    };
    this.summonRings.set(data.instanceId, visual);

    // Infinite pulse — alpha multiplier rises (1.0× → 2.0× of fillAlpha),
    // gentle scale breath, sineInOut yoyo. Matches the prior Phaser tween.
    // An `exact` ring (an AOE's real hit area, plan 29 F6) pulses in alpha
    // only, so it never breathes past the radius it promises.
    this.startRingTween(visual, new Tween(visual.state, {
      to: { alpha: cfg.pulse.alphaMultiplierTo, scale: data.exact ? 1 : cfg.pulse.scaleTo },
      durationMs: cfg.pulse.durationMs,
      yoyo: true,
      repeat: -1,
      ease: Easing.sineInOut,
    }));
  }

  private onSummonTriggered(data: { instanceId: string }): void {
    const visual = this.summonRings.get(data.instanceId);
    if (!visual) return;
    // Flash + expand: alpha → 0, scale up, then destroy.
    this.startRingTween(visual, new Tween(visual.state, {
      to: { alpha: 0, scale: SUMMON_RING_CONFIG.triggered.scaleTo },
      durationMs: SUMMON_RING_CONFIG.triggered.durationMs,
      ease: Easing.power2,
      onComplete: () => {
        visual.gfx.destroy();
        this.summonRings.delete(data.instanceId);
      },
    }));
  }

  private onSummonExpired(data: { instanceId: string }): void {
    const visual = this.summonRings.get(data.instanceId);
    if (!visual) return;
    this.startRingTween(visual, new Tween(visual.state, {
      to: { alpha: 0 },
      durationMs: SUMMON_RING_CONFIG.expired.durationMs,
      onComplete: () => {
        visual.gfx.destroy();
        this.summonRings.delete(data.instanceId);
      },
    }));
  }

  /**
   * Cancel any in-flight ring tween, install the new one, and start a
   * rAF-driven loop that pushes state.alpha + state.scale into the
   * IGraphics each frame.
   */
  private startRingTween(visual: SummonRingVisual, tween: Tween): void {
    if (visual.rafId !== null) cancelAnimationFrame(visual.rafId);
    if (visual.tween) visual.tween.kill();
    visual.tween = tween;

    let lastT = performance.now();
    const tick = () => {
      const now = performance.now();
      const dt = now - lastT;
      lastT = now;

      const alive = tween.step(dt);
      // state.alpha is the multiplier (1.0 = fillAlpha, 2.0 = 2× fillAlpha,
      // 0 = fully transparent). alphaGain lifts the faint fill into the
      // visible setAlpha 0..1 range.
      visual.gfx.setAlpha(Math.max(0, Math.min(1, visual.state.alpha * visual.baseAlpha * SUMMON_RING_CONFIG.alphaGain)));
      visual.gfx.setScale(visual.state.scale);

      if (alive) {
        visual.rafId = requestAnimationFrame(tick);
      } else {
        visual.rafId = null;
      }
    };
    visual.rafId = requestAnimationFrame(tick);
  }

  /**
   * Render-truth rows for the live ground rings — feeds
   * VFXSystem.getRenderedEffects() (the __h2kRenderState test surface). The
   * graphic carries its own world position (setPosition in onSummonPlaced);
   * read defensively off the underlying surface (IGraphics has no position
   * getter, but the Phaser/Pixi impl exposes x/y).
   */
  getRenderedRings(): Array<{ kind: string; instanceId: string; x: number; y: number; effectId: string }> {
    const out: Array<{ kind: string; instanceId: string; x: number; y: number; effectId: string }> = [];
    this.summonRings.forEach((visual, instanceId) => {
      const gfx = visual?.gfx as unknown as { x?: number; y?: number } | undefined;
      out.push({
        kind: 'summon_ring',
        instanceId,
        x: typeof gfx?.x === 'number' ? gfx.x : 0,
        y: typeof gfx?.y === 'number' ? gfx.y : 0,
        effectId: 'summon_ring',
      });
    });
    return out;
  }

  /**
   * K24 (2026-07-17): destroy every live summon ring + armed proximity watch
   * immediately (no fade). Used by VFXSystem.sweepTransient() on in-place
   * server map switches, where placed zones belong to the OLD map and would
   * otherwise pulse forever over the new one (rAF loop + repeat:-1 tween
   * have no other stop path once the placing map is gone).
   */
  clearAll(): void {
    this.summonRings.forEach((visual) => {
      if (visual.rafId !== null) cancelAnimationFrame(visual.rafId);
      visual.tween?.kill();
      try { visual.gfx.destroy(); } catch { /* already destroyed */ }
    });
    this.summonRings.clear();
    this.armedSummons.length = 0;
  }

  /** Armed-summon proximity triggers. Ticked from VFXSystem.update(). */
  update(): void {
    if (this.armedSummons.length === 0) return;
    try {
      // Armed-summon mob-proximity triggers are inert: EntitySystem has no mob
      // accessor (getMobManager was never on it — local mobs live in MobSystem
      // → MobManager). The typed registry surfaced this long-dead path. Reviving
      // it (get('MobSystem')?.getManager()?.getAllMobs()) makes summons trigger
      // on nearby mobs again — a visible gameplay change — so it's deferred to a
      // VQA'd follow-up rather than flipped silently in the type migration.
      const mobs: Array<{ id: string; position?: { x: number; y: number }; x?: number; y?: number }> = [];
      for (let i = this.armedSummons.length - 1; i >= 0; i--) {
        const summon = this.armedSummons[i];
        for (const mob of mobs) {
          if (!mob || mob.id === summon.ownerId) continue;
          const mx = mob.position?.x ?? mob.x ?? 0;
          const my = mob.position?.y ?? mob.y ?? 0;
          const dx = mx - summon.x;
          const dy = my - summon.y;
          if (dx * dx + dy * dy < summon.radius * summon.radius) {
            summon.onTrigger();
            this.armedSummons.splice(i, 1);
            break;
          }
        }
      }
    } catch { /* non-critical */ }
  }
}
